import React, { useEffect, useMemo, useRef } from 'react';

// Hard ceiling on how many matches we highlight in a single body. A short query
// against a large response can match tens of thousands of times; rendering a
// <mark> per match builds a huge DOM and freezes the tab. Beyond this cap we
// stop highlighting and render the rest of the text plain (count still reflects
// the true total via countMatches, which is cheap).
const MAX_HIGHLIGHTS = 2000;

/**
 * Build regex from search query with options.
 */
export function buildSearchRegex(query, options = {}) {
  if (!query) return null;
  try {
    if (options.useRegex) {
      return new RegExp(query, options.caseSensitive ? 'g' : 'gi');
    }
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, options.caseSensitive ? 'g' : 'gi');
  } catch {
    return null;
  }
}

/**
 * Count matches in text for a given regex.
 */
export function countMatches(text, regex) {
  if (!text || !regex) return 0;
  const matches = text.match(new RegExp(regex.source, regex.flags));
  return matches ? matches.length : 0;
}

// Compute match segments once (memoized by text + regex), independent of which
// match is currently active. Stops after MAX_HIGHLIGHTS matches and appends the
// remaining text as a single plain segment so huge match counts can't freeze
// the tab. Returns { segments, matchCount, capped }.
function buildSegments(text, searchRegex, matchOffset) {
  if (!text || !searchRegex) {
    return { segments: text ? [{ type: 'text', value: text }] : [], matchCount: 0, capped: false };
  }
  const segments = [];
  const regex = new RegExp(searchRegex.source, searchRegex.flags);
  let lastIndex = 0;
  let count = 0;
  let match;
  let capped = false;
  while ((match = regex.exec(text)) !== null) {
    if (count >= MAX_HIGHLIGHTS) { capped = true; break; }
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'match', value: match[0], index: matchOffset + count });
    count++;
    lastIndex = regex.lastIndex;
    if (match[0].length === 0) regex.lastIndex++; // guard against zero-length match loops
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return { segments, matchCount: count, capped };
}

/**
 * Renders text with highlighted search matches.
 * Props:
 *   text - the text to render
 *   searchRegex - compiled RegExp (global flag)
 *   matchOffset - starting match index for this panel
 *   activeMatchIndex - globally active match index
 *   className - class for the container pre
 */
const HighlightedText = ({ text, searchRegex, matchOffset = 0, activeMatchIndex = -1, className = '' }) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  // Scroll active match into view
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, [activeMatchIndex]);

  // Rebuild segments only when the text or the regex changes — NOT when the
  // active match index changes (jumping between matches just re-flags one node).
  const { segments } = useMemo(
    () => buildSegments(text, searchRegex, matchOffset),
    [text, searchRegex && searchRegex.source, searchRegex && searchRegex.flags, matchOffset]
  );

  if (!text) return <pre className={className}>{''}</pre>;
  if (!searchRegex) return <pre className={className}>{text}</pre>;

  return (
    <pre className={className} ref={containerRef}>
      {segments.map((part, i) => {
        if (part.type === 'text') return <React.Fragment key={i}>{part.value}</React.Fragment>;
        const isActive = part.index === activeMatchIndex;
        return (
          <mark
            key={i}
            ref={isActive ? activeRef : null}
            style={{
              backgroundColor: isActive ? '#f97316' : '#fbbf24',
              color: '#000',
              borderRadius: '1px',
              padding: '0 1px',
              outline: isActive ? '2px solid #f97316' : 'none'
            }}
          >
            {part.value}
          </mark>
        );
      })}
    </pre>
  );
};

export default HighlightedText;
